import {
  addMonthsLocal,
  formatDateShort,
  formatMoney,
  type PaymentMethod,
  type PaymentStatus,
  type VisitClosingRequest,
  type VisitDetailResponse,
} from '@kominiarz/shared';
import { Check, Save } from 'lucide-react';
import { useState } from 'react';
import { ApiError, errorMessage } from '../../lib/api';
import { local, todayLocal } from '../../lib/calendarDates';
import { useCloseVisit } from '../../lib/visitsApi';
import { Alert } from '../Alert';
import { OfflineNotice } from '../OfflineNotice';
import { useOnline } from '../../lib/appStatus';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { ChoiceGroup } from '../form/ChoiceGroup';
import { Switch } from '../form/Switch';
import { TextArea } from '../form/TextArea';
import { TextInput } from '../form/TextInput';
import { useToast } from '../Toast';
import { PaymentFields } from './PaymentFields';
import { PhotoGallery } from './PhotoGallery';
import { PhotoPicker } from './PhotoPicker';
import styles from './visit.module.css';

interface Props {
  detail: VisitDetailResponse;
  /** close: mark as done now; edit: change the note/payment/next date of a done visit. */
  mode: 'close' | 'edit';
  onClose: () => void;
}

/**
 * Closing a visit on site in one place: note, payment, photos and (for periodic
 * services) the next inspection date, all prefilled so "Zapisz" is often enough.
 */
export function CloseVisitDialog({ detail, mode, onClose }: Props) {
  const v = detail.visit;
  const toast = useToast();
  const close = useCloseVisit();
  const online = useOnline();
  const visitDate = local(v.startAt).toISODate()!;
  const periodicType = v.serviceTypes.find((t) => t.isPeriodic);
  const periodic = periodicType !== undefined;
  const suggestedNext = addMonthsLocal(visitDate, periodicType?.periodMonths ?? 12);

  const [note, setNote] = useState(v.afterNote ?? '');
  const [payment, setPayment] = useState<PaymentStatus>(v.paymentStatus);
  const [method, setMethod] = useState<PaymentMethod | null>(v.paymentMethod);
  const [paidAt, setPaidAt] = useState(v.paidAt ?? todayLocal());
  const [setNext, setSetNext] = useState(periodic && Boolean(v.object));
  const [nextDate, setNextDate] = useState(detail.nextInspectionDue ?? suggestedNext);
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function save() {
    const e: Record<string, string> = {};
    if (payment === 'paid' && !method) e.paymentMethod = 'Wybierz formę płatności.';
    if (setNext && !/^\d{4}-\d{2}-\d{2}$/.test(nextDate)) e.nextInspectionDue = 'Wybierz datę kolejnego przeglądu.';
    setErrors(e);
    if (Object.keys(e).length) return;

    const body: VisitClosingRequest = {
      afterNote: note,
      paymentStatus: payment,
      ...(payment === 'paid' ? { paymentMethod: method, paidAt } : {}),
      ...(mode === 'close' ? { status: 'done' as const } : {}),
      ...(v.object && periodic ? { nextInspectionDue: setNext ? nextDate : null } : {}),
    };
    try {
      await close.mutateAsync({ id: v.id, body });
      toast.success(mode === 'close' ? 'Wizyta wykonana' : 'Zapisano');
      onClose();
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length) setErrors(err.fields);
      else setErrors({ form: errorMessage(err) });
    }
  }

  return (
    <Dialog
      open
      title={mode === 'close' ? 'Wizyta wykonana' : 'Po wizycie'}
      onClose={onClose}
      actions={
        <>
          <Button
            variant="primary"
            size="lg"
            icon={mode === 'close' ? <Check aria-hidden="true" /> : <Save aria-hidden="true" />}
            onClick={save}
            disabled={close.isPending || !online}
          >
            {close.isPending ? 'Zapisywanie…' : mode === 'close' ? 'Zapisz jako wykonaną' : 'Zapisz'}
          </Button>
          <Button variant="secondary" size="lg" onClick={onClose}>
            Anuluj
          </Button>
        </>
      }
    >
      <OfflineNotice />
      <TextArea
        label="Notatka po wizycie"
        optional
        rows={3}
        placeholder="np. przewody drożne, ciąg prawidłowy, protokół wystawiony"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        error={errors.afterNote}
      />

      <fieldset className={styles.group}>
        <legend className={styles.groupTitle}>Płatność{v.amountGrosze ? `: ${formatMoney(v.amountGrosze)}` : ''}</legend>
        <ChoiceGroup<PaymentStatus>
          legend="Czy klient zapłacił?"
          value={payment}
          columns={2}
          options={[
            { value: 'unpaid', label: 'Jeszcze nie' },
            { value: 'paid', label: 'Zapłacił' },
          ]}
          onChange={setPayment}
        />
        {payment === 'paid' && (
          <PaymentFields method={method} paidAt={paidAt} onMethod={setMethod} onPaidAt={setPaidAt} error={errors.paymentMethod} />
        )}
      </fieldset>

      {periodic && v.object && (
        <fieldset className={styles.group}>
          <legend className={styles.groupTitle}>Kolejny przegląd</legend>
          <Switch
            label="Zapisz termin kolejnego przeglądu"
            description={`Adres pojawi się w „Do umówienia” przed tym terminem. Proponowany: ${formatDateShort(suggestedNext)}.`}
            checked={setNext}
            onChange={setSetNext}
          />
          {setNext && (
            <TextInput
              type="date"
              label="Termin kolejnego przeglądu"
              value={nextDate}
              onChange={(e) => setNextDate(e.target.value)}
              error={errors.nextInspectionDue}
            />
          )}
        </fieldset>
      )}
      {periodic && !v.object && (
        <Alert kind="info">Przypisz klienta do wizyty, żeby aplikacja zapamiętała termin kolejnego przeglądu.</Alert>
      )}

      <fieldset className={styles.group}>
        <legend className={styles.groupTitle}>Zdjęcia</legend>
        <PhotoGallery photos={detail.photos} />
        <PhotoPicker visitId={v.id} />
      </fieldset>

      {errors.form && <Alert kind="error">{errors.form}</Alert>}
    </Dialog>
  );
}
